import React, { useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { Visible } from "../../../core/components";
import { useUpsertQuote } from "../../../order/hooks/quotes";
import { CostFields } from "../../../listings/atoms";
import { FieldGroup } from "../../../components";
import { currencyOptions } from "../../../core/currency";
import FormikAutoSave from "./FormikAutoSave";
import { Badge, Button } from "../../../components/ui";

const costsCurrencyOptions = () => currencyOptions();

// A cleared field is "no cost", not an invalid number — otherwise emptying a
// field could never be saved.
const cost = () =>
  Yup.number()
    .transform((value, original) => (original === "" || original === null ? undefined : value))
    .typeError("Invalid");

const validationSchema = Yup.object().shape({
  polishing: cost(),
  accessories: cost(),
  shipping: cost(),
  travel: cost(),
});

const costsFields = [
  "costs_currency",
  "polishing",
  "accessories",
  "shipping",
  "travel",
];

// The quote workspace's Costs: the same fields, labelled above, in its grid.
const CostsGrid = () => (
  // Currency alone in the first column; Polishing / Accessories and Shipping /
  // Travel stacked two to a column beside it (quote-workspace.scss).
  <div className="qws-form-grid qws-form-grid--costs">
    <div className="qws-field qws-field--currency">
      <FieldGroup name="costs_currency" type="select" options={costsCurrencyOptions()} label="Currency" placeholder="Select…" />
    </div>
    {[
      ["polishing", "Polishing"],
      ["accessories", "Accessories"],
      ["shipping", "Shipping"],
      ["travel", "Travel"],
    ].map(([name, label]) => (
      <div className={`qws-field qws-field--${name}`} key={name}>
        <FieldGroup name={name} type="numberFormat" label={label} placeholder="0" />
      </div>
    ))}
  </div>
);

// autoSave: the quote workspace (new setup) — saves on change, no Save button.
// layout="grid": the workspace's labelled grid; anything else is the modal's.
export function CostsForm({ id, quote, autoSave = false, layout = "legacy" }) {
  const { upsertQuote } = useUpsertQuote();
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const initialValues = useMemo(
    () =>
      Object.keys(quote.costs || {}).reduce(
        (pd, key) => ({
          ...pd,
          [key]:
            key === "costs_currency"
              ? quote.costs[key] || quote.currency || "gbp"
              : costsFields.includes(key)
              ? quote.costs[key] / 100
              : quote.costs[key],
        }),
        {}
      ),
    [quote.costs]
  );

  const handleSubmit = async (data) => {
    setError(null);

    const costsData = Object.keys(data).reduce(
      (md, key) => ({
        ...md,
        [key]:
          key === "costs_currency"
            ? data[key]
            : parseFloat(data[key]) * (costsFields.includes(key) ? 100 : 1),
      }),
      {}
    );

    try {
      await upsertQuote({
        id,
        quoteData: {
          costs: costsData,
          product_id: quote.product_id,
          user_id: quote.user_id,
          currency: quote.currency,
          price: quote.price,
          date: quote.date,
          // No min_price / max_price here. They were copied from PricingForm,
          // whose fields (after-fee min/max, fee) this form does not have: the
          // sums were NaN, sent as null, and every Costs save wiped the quote's
          // Consign min / max.
        },
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setError(
        error?.response?.data?.errors?.[0]?.[0] ||
          "The quote costs could not be saved"
      );
    }
  };

  return (
    <div className="quote-form">
      <Formik
        {...{
          initialValues,
          validationSchema,
          onSubmit: handleSubmit,
        }}
      >
        <Form>
          {layout === "grid" ? <CostsGrid /> : <CostFields />}

          <Visible when={error}>
            <div className="error">{error}</div>
          </Visible>

          {autoSave ? (
            <FormikAutoSave />
          ) : (
          <div className="row">
            <div className="col-12 col-sm-3 p-1"></div>
            <div className="col-12 col-sm-3 p-1"></div>
            <div className="col-12 col-sm-3 p-1">
              {showSuccess && (
                <Badge tone="success">Saved</Badge>
              )}
            </div>
            <div className="col-12 col-sm-3 p-1">
              <Button variant="primary" size="lg" fullWidth type="submit" data-btn="quote-costs-save">
                Save
              </Button>
            </div>
          </div>
          )}
        </Form>
      </Formik>
    </div>
  );
}
